import React, { useEffect, useState } from 'react';
import { contractsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, FileText, Download, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function MyContract() {
  const { refreshUser } = useAuth();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);

  const load = () => {
    setLoading(true);
    contractsApi.getMy().then(r => setContract(r.data)).catch(() => setContract(null)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sign = async () => {
    setSigning(true);
    try {
      const r = await contractsApi.sign();
      toast.success(`Contrato firmado. Hash: ${r.data.signature_hash.slice(0, 12)}...`);
      await refreshUser();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al firmar'); }
    finally { setSigning(false); }
  };

  const download = async () => {
    if (!contract?.id) return;
    try {
      const r = await contractsApi.downloadPdf(contract.id);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'contrato_aea.pdf'; a.click();
    } catch { toast.error('Error al descargar'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-aea-blue" /></div>;
  if (!contract) return <div className="text-center py-10 text-slate-500">No hay contrato asociado a su cuenta.</div>;

  const isSigned = contract.status === 'signed';

  return (
    <div className="max-w-3xl animate-fade-in-up">
      <Card className="aea-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-aea-blue" /> Contrato de adhesión</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Adhesión al Espacio de Datos AEA - Asociación Española del Aluminio.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-500">Estado:</span>
              {isSigned ? <span className="status-badge status-signed"><CheckCircle2 className="w-3 h-3 mr-1" /> Firmado</span> : <span className="status-badge status-pending">Pendiente firma</span>}
            </div>
            {isSigned && (
              <>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Firmado el:</span><span className="font-mono text-xs">{new Date(contract.signed_at).toLocaleString('es-ES')}</span></div>
                <div className="text-sm"><span className="text-slate-500">Hash SHA-256:</span><div className="font-mono text-[10px] break-all mt-1 text-slate-600">{contract.signature_hash}</div></div>
              </>
            )}
          </div>

          <div className="prose prose-sm max-w-none text-slate-700">
            <h3 className="font-outfit">Cláusulas principales</h3>
            <ol>
              <li><strong>Propiedad de los datos.</strong> Su empresa conserva en todo momento la propiedad de los datos aportados.</li>
              <li><strong>Uso permitido.</strong> AEA únicamente utilizará sus datos para elaborar estadísticas sectoriales agregadas y anonimizadas.</li>
              <li><strong>Confidencialidad.</strong> En ningún caso se compartirán datos individuales de una empresa con otras empresas.</li>
              <li><strong>Anonimato (k≥3).</strong> Las comparativas requieren un mínimo de 3 empresas.</li>
              <li><strong>Baja.</strong> Puede solicitar la baja del Espacio de Datos en cualquier momento.</li>
              <li><strong>RGPD.</strong> El tratamiento cumple el RGPD y LOPDGDD.</li>
            </ol>
          </div>

          <div className="flex gap-3">
            {!isSigned ? (
              <Button onClick={sign} disabled={signing} className="bg-aea-blue hover:bg-aea-blue-dk" data-testid="btn-sign-contract">
                {signing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Firmar contrato
              </Button>
            ) : (
              <Button variant="outline" onClick={download} data-testid="btn-download-contract"><Download className="w-4 h-4 mr-2" /> Descargar PDF firmado</Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
